import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Divider,
    Grid,
    TextField,
    CircularProgress,
    Snackbar,
    Paper,
    Tooltip,
    Icon,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DescriptionIcon from "@material-ui/icons/Description";
import BusinessIcon from "@material-ui/icons/Business";
import ProblemIcon from "@material-ui/icons/ReportProblem";
import PeopleIcon from "@material-ui/icons/People";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";
import NotesIcon from "@material-ui/icons/Notes";
import Button from "components/CustomButtons/Button.js";
// If your API utilities live elsewhere, adjust the import path accordingly.
import { getCaseStudyAPI, backgroundStepAPI } from "views/APIs/CaseStudyUtility";
import NotificationContext from "views/Components/Context.js";



const useStyles = makeStyles((theme) => ({
    root: {
        position: "relative",
    },
    header: {
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3),
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        boxShadow: theme.shadows[2],
    },
    headerTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    section: {
        marginTop: theme.spacing(3),
    },
    paper: {
        padding: theme.spacing(3),
        borderRadius: 16,
        boxShadow: theme.shadows[2],
    },
    fieldCard: {
        padding: theme.spacing(2.25),
        borderRadius: 14,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.palette.background.paper,
        transition: "box-shadow 120ms ease, transform 120ms ease",
        "&:focus-within": {
            boxShadow: theme.shadows[4],
            transform: "translateY(-1px)",
        },
    },
    helper: {
        display: "flex",
        justifyContent: "space-between",
    },
    actionsBar: {
        position: "sticky",
        bottom: 0,
        background: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 2),
        marginTop: theme.spacing(2),
        display: "flex",
        justifyContent: "flex-end",
        gap: theme.spacing(1),
        zIndex: 1,
    },
}));

/**
 * Background
 * Material-UI v4 • React 18 compatible
 *
 * Props
 * - caseStudyID: string (required)
 * - caseStudy: object (required)
 * - loading: boolean (optional)
 * - setActiveStep(stepIndex: number): void (optional)
 * - onSave(payload): void (optional) — called after successful Update
 * - onNext(payload): void (optional) — called after successful Next
 */
const Background = ({
    caseStudyID,
    caseStudy = {},
    loading = false,
    setActiveStep = () => { },
    onSave = () => { },
    onNext = () => { },
    caseStudyDetails = {},
    setCaseStudyDetails = () => { },
}) => {
    const classes = useStyles();
    const { showNotification } = React.useContext(NotificationContext);

    const initial = useMemo(
        () => ({
            case_subject: caseStudy?.background?.case_subject || "",
            industry: caseStudy?.background?.industry || "",
            context: caseStudy?.background?.context || "",
            primary_problem: caseStudy?.background?.primary_problem || "",
            target_audience: caseStudy?.background?.target_audience || "",
            case_focus: caseStudy?.background?.case_focus || "",
        }),
        [caseStudy?.background]
    );

    const [form, setForm] = useState(initial);



    useEffect(() => {
        setForm(initial);
    }, [initial]);

    // Optional refresh
    // useEffect(() => {
    //   let mounted = true;
    //   (async () => {
    //     if (!caseStudyID) return;
    //     try {
    //       const fresh = await getCaseStudyAPI(caseStudyID);
    //       if (!mounted) return;
    //       const bg = fresh?.background || {};
    //       setForm({
    //         case_subject: bg.case_subject || "",
    //         industry: bg.industry || "",
    //         context: bg.context || "",
    //         primary_problem: bg.primary_problem || "",
    //         target_audience: bg.target_audience || "",
    //         case_focus: bg.case_focus || "",
    //       });
    //     } catch {}
    //   })();
    //   return () => { mounted = false; };
    // }, [caseStudyID]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setCaseStudyDetails((prev) => ({ ...prev, background: { ...prev.background, [name]: value } }));
    };

    const requiredFilled = useMemo(
        () =>
            form.case_subject.trim() &&
            form.industry.trim() &&
            form.primary_problem.trim() &&
            form.target_audience.trim(),
        [form]
    );


    const handleUpdate = async (e) => {
        e.preventDefault();
        setCaseStudyDetails({ ...caseStudyDetails, background: form });
        onSave();
    };

    const handleNext = async () => {
        setCaseStudyDetails({ ...caseStudyDetails, background: form });
        onNext();
    };

    const count = (val, max) => `${val.length}${max ? `/${max}` : ""}`;

    return (
        <Box className={classes.root} component="section">
            {/* Fancy header */}
            <Typography variant="h5">
            Case Context & Details
            </Typography>
            <Typography variant="body1">
            Establish core context—specify the case subject, industry, operating environment, primary problem, target audience, and case focus to frame the narrative.
            </Typography>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />

            {/* Content card */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" display="block"><DescriptionIcon fontSize="small" style={{ verticalAlign: "text-bottom", }} /> Case Subject</Typography>
                    <TextField
                        placeholder="E.g., Payment fraud surge at a fintech"
                        name="case_subject"
                        value={form.case_subject}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        required
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="body2" display="block"><BusinessIcon fontSize="small" style={{ verticalAlign: "text-bottom" }} /> Industry</Typography>
                    <TextField
                        placeholder="Banking, FinTech, Healthcare, Retail"
                        name="industry"
                        value={form.industry}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        required
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" display="block"><NotesIcon fontSize="small" style={{ verticalAlign: "text-bottom" }} /> Context</Typography>
                    <TextField
                        placeholder="Backdrop, constraints, and key actors"
                        name="context"
                        value={form.context}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 1200 }}
                        variant="outlined"
                        helperText={<span className={classes.helper}>Backdrop, constraints, and key actors<span>{count(form.context, 1200)}</span></span>}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" display="block"><ProblemIcon fontSize="small" style={{ verticalAlign: "text-bottom" }} /> Primary Problem</Typography>
                    <TextField
                        placeholder="What is the core challenge?"
                        name="primary_problem"
                        value={form.primary_problem}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        multiline
                        rows={3}
                        inputProps={{ maxLength: 600 }}
                        variant="outlined"
                        helperText={<span className={classes.helper}>What is the core challenge?<span>{count(form.primary_problem, 600)}</span></span>}
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="body2" display="block"><PeopleIcon fontSize="small" style={{ verticalAlign: "text-bottom" }} /> Target Audience</Typography>
                    <TextField
                        placeholder="Risk ops leaders, data scientists, executives"
                        name="target_audience"
                        value={form.target_audience}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        required
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="body2" display="block"><CenterFocusStrongIcon fontSize="small" style={{ verticalAlign: "text-bottom" }} /> Case Focus</Typography>
                    <TextField
                        placeholder="Strategy, process, data, technology, org design, etc."
                        name="case_focus"
                        value={form.case_focus}
                        onChange={handleChange}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                    />
                </Grid>
            </Grid>

            {/* Sticky actions */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginLeft: 'auto',
                marginTop: '16px'
            }}>
                <Button
                    color="quCreateColor"
                    onClick={handleUpdate}
                    disabled={loading || !requiredFilled}
                    style={{ marginRight: 10 }}
                >
                    <Icon>save</Icon>    {loading ? <CircularProgress size={20} /> : "Save Changes"}
                </Button>
                <Button
                    color="quCreateColor"
                    variant="contained"
                    onClick={handleNext}
                    disabled={loading || !requiredFilled}
                >
                    {loading ? <CircularProgress size={20} /> : "Next"}
                </Button>
            </div>

        </Box >
    );
};

export default Background;
